import { useCallback, useMemo } from 'react';
import { useSearchParams as useSearchParamsNext } from 'next/navigation';

import type {
	ACCOUNT_ORDER_STATUS_CUSTOMER_ID_QUERY_PARAM,
	ACCOUNT_ORDER_STATUS_ORDER_ID_QUERY_PARAM,
	BLOG_OVERVIEW_OFFSET_QUERY_PARAM,
	BLOG_OVERVIEW_PAGE_SIZE_QUERY_PARAM,
	BLOG_OVERVIEW_TAG_QUERY_PARAM,
	BRAND_QUERY_PARAM,
	CHECKOUT_CART_ID_QUERY_PARAM,
	CHECKOUT_REDIRECT_RESULTS_QUERY_PARAM,
	CHECKOUT_RESULT_CODE_QUERY_PARAM,
	CHECKOUT_TYPE_QUERY_PARAM,
	CHECKOUT_VERIFY_REDIRECT_PAYMENT_QUERY_PARAM,
	INSPIRATION_OVERVIEW_OFFSET_QUERY_PARAM,
	INSPIRATION_OVERVIEW_PAGE_SIZE_QUERY_PARAM,
	INSPIRATION_OVERVIEW_TAG_QUERY_PARAM,
	JURIDICAL_TYPE_QUERY_PARAM,
	NOTIFY_LOGIN_USER_ACTION_QUERY_PARAM,
	OIDC_REDIRECT_URL_AUTH_CODE_QUERY_PARAM,
	OIDC_REDIRECT_URL_ERROR_QUERY_PARAM,
	OIDC_REDIRECT_URL_IS_LOGIN_QUERY_PARAM,
	ORDER_ID_QUERY_PARAM,
	PRODUCT_LIST_OFFSET_QUERY_PARAM,
	PRODUCT_LIST_PAGE_SIZE_QUERY_PARAM,
	PRODUCT_LIST_SORTING_QUERY_PARAM,
	PROTECTED_ROUTE_RETURN_PATH_QUERY_PARAM,
	SEARCH_QUERY_PARAM,
} from 'constants/queryParams';
import type { CustomerId } from 'models/api';
import type { ConnectJuridicalType } from 'models/connect/oauth';
import type { NotifyLoginUserActionType } from 'models/internalApi';
import type { CartId, TransactionId } from 'types';
import type { OIDCError } from 'utils/business-logic';
import { asArray } from 'utils/helpers';
import { createUrl, type URLParams } from 'utils/url';

import { usePath } from './usePath';
import { useUrlUpdate } from './useUrlUpdate';

export interface ParamValue {
	[ACCOUNT_ORDER_STATUS_CUSTOMER_ID_QUERY_PARAM]: CustomerId;
	[ACCOUNT_ORDER_STATUS_ORDER_ID_QUERY_PARAM]: TransactionId;
	[BLOG_OVERVIEW_OFFSET_QUERY_PARAM]: string;
	[BLOG_OVERVIEW_PAGE_SIZE_QUERY_PARAM]: string;
	[BLOG_OVERVIEW_TAG_QUERY_PARAM]: string;
	[BRAND_QUERY_PARAM]: string;
	[CHECKOUT_CART_ID_QUERY_PARAM]: CartId;
	[CHECKOUT_REDIRECT_RESULTS_QUERY_PARAM]: string;
	[CHECKOUT_RESULT_CODE_QUERY_PARAM]: string;
	[CHECKOUT_TYPE_QUERY_PARAM]: string;
	[CHECKOUT_VERIFY_REDIRECT_PAYMENT_QUERY_PARAM]: string;
	[INSPIRATION_OVERVIEW_OFFSET_QUERY_PARAM]: string;
	[INSPIRATION_OVERVIEW_PAGE_SIZE_QUERY_PARAM]: string;
	[INSPIRATION_OVERVIEW_TAG_QUERY_PARAM]: string;
	[JURIDICAL_TYPE_QUERY_PARAM]: ConnectJuridicalType;
	[NOTIFY_LOGIN_USER_ACTION_QUERY_PARAM]: NotifyLoginUserActionType;
	[OIDC_REDIRECT_URL_AUTH_CODE_QUERY_PARAM]: string;
	[OIDC_REDIRECT_URL_ERROR_QUERY_PARAM]: OIDCError;
	[OIDC_REDIRECT_URL_IS_LOGIN_QUERY_PARAM]: string;
	[ORDER_ID_QUERY_PARAM]: CartId;
	[PRODUCT_LIST_OFFSET_QUERY_PARAM]: string;
	[PRODUCT_LIST_PAGE_SIZE_QUERY_PARAM]: string;
	[PRODUCT_LIST_SORTING_QUERY_PARAM]: string;
	[PROTECTED_ROUTE_RETURN_PATH_QUERY_PARAM]: string;
	[SEARCH_QUERY_PARAM]: string;
}

type Param = keyof ParamValue;

export function useSearchParams() {
	const searchParams = useSearchParamsNext();

	return useMemo(
		() => ({
			get: <P extends Param>(param: P) =>
				(searchParams.get(param) || undefined) as ParamValue[P] | undefined,
			getAll: <P extends Param>(param: P) =>
				searchParams.getAll(param) as ParamValue[P][],
			hasValue: <P extends Param>(param: P) => Boolean(searchParams.get(param)),
			entries: () => searchParams.entries(),
		}),
		[searchParams],
	);
}

function useUpdateSearchParams() {
	const { pathname } = usePath();
	const searchParams = useSearchParamsNext();
	const { updateUrl } = useUrlUpdate();

	return useCallback(
		(params: URLParams, { keepExisting }: { keepExisting: boolean }) => {
			// Handle multiple values for the same key (e.g. `?brand=A&brand=B`).
			const existingParams = keepExisting
				? Object.fromEntries(
						[...new Set(searchParams.keys())].map((key) => {
							const values = searchParams.getAll(key);
							return [key, values.length > 1 ? values : values[0]];
						}),
					)
				: {};
			updateUrl(
				createUrl(
					pathname,
					{ ...existingParams, ...params },
					{ replaceParams: true },
				),
			);
		},
		[pathname, searchParams, updateUrl],
	);
}

export function useDeleteSearchParams() {
	const updateSearchParams = useUpdateSearchParams();

	const deleteSearchParams = useCallback(
		(param: Param | Param[]) => {
			updateSearchParams(
				Object.fromEntries(asArray(param).map((k) => [k, null])),
				{ keepExisting: true },
			);
		},
		[updateSearchParams],
	);

	return {
		/**
		 * Delete the specified search params from the URL.
		 *
		 * NOTE! Deletes all values for the given param(s), e.g. passing 'brand'
		 * will remove both brands from `?brand=A&brand=B`.
		 */
		deleteSearchParams,
	};
}

export function useSetSearchParams() {
	const updateSearchParams = useUpdateSearchParams();

	const mergeSearchParams = useCallback(
		(params: Partial<URLParams<Param>>) => {
			updateSearchParams(params, { keepExisting: true });
		},
		[updateSearchParams],
	);

	const replaceSearchParams = useCallback(
		(params: Partial<URLParams<Param>>) => {
			updateSearchParams(params, { keepExisting: false });
		},
		[updateSearchParams],
	);

	return {
		/**
		 * Update the specified search params in the URL, keeping any existing.
		 */
		mergeSearchParams,
		/**
		 * Replace all search params in the URL with the specified ones,
		 * ignoring any existing.
		 */
		replaceSearchParams,
	};
}
